import React, { useState, useEffect } from 'react';
import {
  HiOutlineStatusOnline,
  HiOutlineStatusOffline,
  HiOutlineCog,
  HiOutlineTrash,
  HiOutlineLightBulb
} from 'react-icons/hi';
import { IoFlashOutline } from 'react-icons/io5';
import { TbTemperature, TbDroplet, TbLink, TbLinkOff } from 'react-icons/tb';
import { FiPower } from 'react-icons/fi';

function DeviceCard({ device, onControl, onDelete, onAutomation }) {
  // Trigger röleleri için state
  const [triggerStates, setTriggerStates] = useState({});
  const [triggerAnimations, setTriggerAnimations] = useState({});
  
  // Lamba grupları için state
  const [lampStates, setLampStates] = useState({});
  const [lampAnimations, setLampAnimations] = useState({});

  // localStorage'dan trigger state'lerini yükle
  useEffect(() => {
    const storageKey = `trigger_states_${device._id}`;
    const savedStates = localStorage.getItem(storageKey);
    
    if (savedStates) {
      setTriggerStates(JSON.parse(savedStates));
    } else {
      const initialStates = {};
      device.relays.forEach(relay => {
        if (relay.relayType === 'trigger') {
          initialStates[relay.relayNumber] = false;
        }
      });
      setTriggerStates(initialStates);
    }
  }, [device._id]);

  // Lamba state'lerini device.relays'den al (DB'den gelen güncel durum)
  useEffect(() => {
    const newLampStates = {};
    const lampGroups = [...new Set(device.relays.filter(r => r.lampGroup).map(r => r.lampGroup))];

    lampGroups.forEach(group => {
      // lamp_open rölesinin currentState'ine bak - true ise lamba açık
      const openRelay = device.relays.find(r => r.lampGroup === group && r.relayType === 'lamp_open');
      newLampStates[group] = openRelay?.currentState || false;
    });

    setLampStates(newLampStates);
  }, [device._id, device.relays]);

  // State değişince localStorage'a kaydet
  useEffect(() => {
    if (Object.keys(triggerStates).length > 0) {
      const storageKey = `trigger_states_${device._id}`;
      localStorage.setItem(storageKey, JSON.stringify(triggerStates));
    }
  }, [triggerStates, device._id]);

  // Lamba durumları artık DB'den okunuyor, localStorage'a kaydetmeye gerek yok

  // Trigger butona basıldığında
  const handleTrigger = (relayNumber) => {
    setTriggerAnimations(prev => ({...prev, [relayNumber]: true}));
    onControl(device._id, relayNumber, 'trigger');
    setTriggerStates(prev => ({...prev, [relayNumber]: !prev[relayNumber]}));
    
    setTimeout(() => {
      setTriggerAnimations(prev => ({...prev, [relayNumber]: false}));
    }, 2000);
  };

  // Lamba kontrolü - tek butonla aç/kapat
  const handleLampControl = (lampGroup) => {
    const openRelay = device.relays.find(r => r.lampGroup === lampGroup && r.relayType === 'lamp_open');
    const closeRelay = device.relays.find(r => r.lampGroup === lampGroup && r.relayType === 'lamp_close');
    
    if (!openRelay || !closeRelay) return;
    
    const currentState = lampStates[lampGroup] || false;
    
    // Animasyon başlat
    setLampAnimations(prev => ({...prev, [lampGroup]: true}));
    
    if (currentState) {
      // Açıksa KAPAT rölesini tetikle
      onControl(device._id, closeRelay.relayNumber, 'trigger');
    } else {
      // Kapalıysa AÇ rölesini tetikle
      onControl(device._id, openRelay.relayNumber, 'trigger');
    }
    
    // State'i değiştir
    setLampStates(prev => ({...prev, [lampGroup]: !currentState}));
    
    // 2 saniye sonra animasyonu kaldır
    setTimeout(() => {
      setLampAnimations(prev => ({...prev, [lampGroup]: false}));
    }, 2000);
  };

  // TOPLU KONTROL - Tüm toggle rölelerini aç/kapat
  const controlAllToggleRelays = (action) => {
    const toggleRelays = device.relays.filter(r => r.relayType === 'toggle' && r.isEnabled);
    
    if (toggleRelays.length === 0) {
      alert('Bu cihazda toplu kontrol edilebilecek toggle röle yok!');
      return;
    }
    
    toggleRelays.forEach(relay => {
      onControl(device._id, relay.relayNumber, action);
    });
  };

  // SMART TOPLU KONTROL - Trigger röleler için toggle mantığı
  const smartControlAllTriggers = () => {
    const triggerRelays = device.relays.filter(r => r.relayType === 'trigger' && r.isEnabled);
    
    if (triggerRelays.length === 0) {
      alert('Bu cihazda toplu kontrol edilebilecek trigger röle yok!');
      return;
    }
    
    const allOpen = triggerRelays.every(relay => triggerStates[relay.relayNumber] === true);
    
    const newStates = {...triggerStates};
    const newAnimations = {...triggerAnimations};
    
    triggerRelays.forEach(relay => {
      newAnimations[relay.relayNumber] = true;
      onControl(device._id, relay.relayNumber, 'trigger');
      newStates[relay.relayNumber] = !triggerStates[relay.relayNumber];
      
      setTimeout(() => {
        setTriggerAnimations(prev => ({...prev, [relay.relayNumber]: false}));
      }, 2000);
    });
    
    setTriggerStates(newStates);
    setTriggerAnimations(newAnimations);
  };

  // TOPLU LAMBA KONTROLÜ
  const controlAllLamps = () => {
    const lampGroups = [...new Set(device.relays.filter(r => r.lampGroup).map(r => r.lampGroup))];
    
    if (lampGroups.length === 0) return;
    
    const allOpen = lampGroups.every(group => lampStates[group] === true);
    
    lampGroups.forEach(group => {
      setLampAnimations(prev => ({...prev, [group]: true}));
      
      const openRelay = device.relays.find(r => r.lampGroup === group && r.relayType === 'lamp_open');
      const closeRelay = device.relays.find(r => r.lampGroup === group && r.relayType === 'lamp_close');
      
      if (allOpen && closeRelay) {
        onControl(device._id, closeRelay.relayNumber, 'trigger');
      } else if (!allOpen && openRelay) {
        onControl(device._id, openRelay.relayNumber, 'trigger');
      }
      
      setTimeout(() => {
        setLampAnimations(prev => ({...prev, [group]: false}));
      }, 2000);
    });
    
    const newStates = {};
    lampGroups.forEach(group => {
      newStates[group] = !allOpen;
    });
    setLampStates(prev => ({...prev, ...newStates}));
  };

  // Röle tiplerini kontrol et
  const hasToggleRelays = device.relays.some(r => r.relayType === 'toggle' && r.isEnabled);
  const hasTriggerRelays = device.relays.some(r => r.relayType === 'trigger' && r.isEnabled);
  const hasLampRelays = device.relays.some(r => r.relayType === 'lamp_open' || r.relayType === 'lamp_close');
  
  const allTriggersOpen = device.relays
    .filter(r => r.relayType === 'trigger' && r.isEnabled)
    .every(relay => triggerStates[relay.relayNumber] === true);

  // Lamba gruplarını bul
  const lampGroups = [...new Set(device.relays.filter(r => r.lampGroup).map(r => r.lampGroup))];
  const allLampsOpen = lampGroups.every(group => lampStates[group] === true);

  // Normal röleler (lamba olmayan)
  const normalRelays = device.relays.filter(r => !r.lampGroup);

  return (
    <div className="device-item">
      <div className="device-header">
        <span className="device-name">{device.deviceName}</span>
        <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
          <span className={`device-status ${device.isOnline ? 'status-online' : 'status-offline'}`} style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
            {device.isPaired ? (
              device.isOnline ? <><HiOutlineStatusOnline size={14} /> ONLİNE</> : <><HiOutlineStatusOffline size={14} /> OFFLİNE</>
            ) : (
              <><TbLinkOff size={14} /> EŞLEŞMEMIŞ</>
            )}
          </span>
          <button
            className="btn-automation"
            onClick={() => onAutomation && onAutomation(device._id)}
            title="Otomasyon Ayarları"
            style={{
              background: '#FF9800',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <HiOutlineCog size={14} /> OTOMASYON
          </button>
          {onDelete && (
            <button
              className="btn-delete btn-small"
              onClick={() => onDelete(device._id)}
              title="Cihazı Sil"
            >
              <HiOutlineTrash size={16} />
            </button>
          )}
        </div>
      </div>

      {device.hasDHT && (
        <div className="device-sensor">
          <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
            <TbTemperature size={18} color="#E63946" /> {device.temperature ? `${device.temperature}°C` : '--'}
          </span>
          <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
            <TbDroplet size={18} color="#3B82F6" /> {device.humidity ? `${device.humidity}%` : '--'}
          </span>
        </div>
      )}

      {/* TOPLU KONTROL BUTONLARI - TOGGLE */}
      {hasToggleRelays && (
        <div style={{display: 'flex', gap: '8px', marginBottom: '15px', marginTop: '10px'}}>
          <button
            onClick={() => controlAllToggleRelays('on')}
            className="btn-on btn-small"
            disabled={!device.isPaired}
            style={{flex: 1, padding: '8px 12px', fontSize: '12px'}}
          >
            TOPLU AÇ (Toggle)
          </button>
          <button
            onClick={() => controlAllToggleRelays('off')}
            className="btn-off btn-small"
            disabled={!device.isPaired}
            style={{flex: 1, padding: '8px 12px', fontSize: '12px'}}
          >
            TOPLU KAPAT (Toggle)
          </button>
        </div>
      )}

      {/* SMART TOPLU KONTROL - TRIGGER */}
      {hasTriggerRelays && (
        <div style={{display: 'flex', gap: '8px', marginBottom: '15px', marginTop: '10px'}}>
          <button
            onClick={smartControlAllTriggers}
            className={allTriggersOpen ? 'btn-trigger-on' : 'btn-trigger-off'}
            disabled={!device.isPaired}
            style={{
              width: '100%',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <IoFlashOutline size={16} /> {allTriggersOpen ? 'TOPLU TETİKLE (KAPAT)' : 'TOPLU TETİKLE (AÇ)'}
          </button>
        </div>
      )}

      {/* TOPLU LAMBA KONTROLÜ */}
      {hasLampRelays && lampGroups.length > 1 && (
        <div style={{display: 'flex', gap: '8px', marginBottom: '15px', marginTop: '10px'}}>
          <button
            onClick={controlAllLamps}
            style={{
              width: '100%',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: '600',
              background: allLampsOpen ? '#4CAF50' : '#E63946',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
            disabled={!device.isPaired}
          >
            <HiOutlineLightBulb size={18} /> {allLampsOpen ? 'TÜM LAMBALARI KAPAT' : 'TÜM LAMBALARI AÇ'}
          </button>
        </div>
      )}

      <div className="relay-list">
        {/* LAMBA GRUPLARI */}
        {lampGroups.map(group => {
          const openRelay = device.relays.find(r => r.lampGroup === group && r.relayType === 'lamp_open');
          const lampName = openRelay?.relayName?.replace(' - AÇ', '') || `Lamba ${group}`;
          const isOpen = lampStates[group] || false;
          const isAnimating = lampAnimations[group] || false;
          
          return (
            <div key={`lamp-${group}`} className="relay-item">
              <span className="relay-name" style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                <HiOutlineLightBulb size={18} color="#F59E0B" /> {lampName}
              </span>
              <div className="relay-controls">
                <button
                  onClick={() => handleLampControl(group)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    background: isAnimating ? '#FF9800' : (isOpen ? '#4CAF50' : '#E63946'),
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  disabled={!device.isPaired}
                >
                  {isAnimating
                    ? <><IoFlashOutline size={16} /> ÇALIŞTIRILIYOR...</>
                    : isOpen
                      ? <><FiPower size={16} /> AÇIK</>
                      : <><FiPower size={16} /> KAPALI</>
                  }
                </button>
              </div>
            </div>
          );
        })}

        {/* NORMAL RÖLELER */}
        {normalRelays.map(relay => (
          <div key={relay._id} className="relay-item">
            <span className="relay-name">
              {relay.relayName}
            </span>
            {relay.relayType === 'trigger' ? (
              <div className="relay-controls">
                <button
                  onClick={() => handleTrigger(relay.relayNumber)}
                  className={
                    triggerAnimations[relay.relayNumber]
                      ? 'btn-trigger-active'
                      : triggerStates[relay.relayNumber]
                        ? 'btn-trigger-on'
                        : 'btn-trigger-off'
                  }
                  disabled={!device.isPaired || !relay.isEnabled}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  {triggerAnimations[relay.relayNumber]
                    ? <><IoFlashOutline size={16} /> TETİKLENİYOR...</>
                    : triggerStates[relay.relayNumber]
                      ? <><FiPower size={16} /> AÇIK</>
                      : <><FiPower size={16} /> KAPALI</>
                  }
                </button>
              </div>
            ) : (
              <div className="relay-controls">
                <div
                  className={`toggle-switch ${relay.currentState ? 'on' : 'off'}`}
                  onClick={() => onControl(device._id, relay.relayNumber, relay.currentState ? 'off' : 'on')}
                  style={{
                    opacity: (!device.isPaired || !relay.isEnabled) ? 0.5 : 1,
                    cursor: (!device.isPaired || !relay.isEnabled) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <div className="toggle-slider" style={{pointerEvents: "none"}}></div>
                  <span className="toggle-label" style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                    <FiPower size={14} /> {relay.currentState ? 'AÇIK' : 'KAPALI'}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default DeviceCard;
